import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import type { NoteQueues, PendingUpdate } from '@/api/types';
import { RecordNoteOnBehalf } from '@/components/RecordNoteOnBehalf';
import {
  Button,
  Card,
  CardHead,
  ErrorState,
  Loading,
  Notice,
  Page,
  PageHead,
  Tag,
  Table,
  Tabs,
} from '@/components/ui';
import { formatDate, formatRelative, humanise } from '@/lib/format';

const QUEUES = [
  { value: 'late', label: 'Late' },
  { value: 'missing', label: 'Missing' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'edited', label: 'Corrected' },
  { value: 'submitted', label: 'Submitted' },
] as const;

type QueueKey = (typeof QUEUES)[number]['value'];

/**
 * ADM-03's five queues, plus the family-update approval step.
 *
 * The approval step is here rather than hidden in a settings screen because it
 * is the gate between a submitted note and anything a family sees — which
 * makes it a queue, not a preference.
 */
export function NotesPage() {
  const navigate = useNavigate();
  const queues = useApi<NoteQueues>('/admin/notes/queues');
  const pending = useApi<{ pending: PendingUpdate[] }>('/admin/family-updates/pending');
  const [queue, setQueue] = useState<QueueKey>('late');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [recordingFor, setRecordingFor] = useState<string | null>(null);

  if (queues.loading) return <Loading />;
  if (queues.error)
    return <Page><ErrorState message={queues.error.message} onRetry={queues.reload} /></Page>;

  async function decide(updateId: string, decision: 'approve' | 'withhold') {
    setBusyId(updateId);
    setProblem(null);
    try {
      await api.post(`/admin/family-updates/${updateId}/${decision}`);
      pending.reload();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusyId(null);
    }
  }

  const items = queues.data?.queues[queue] ?? [];
  const awaiting = pending.data?.pending ?? [];

  return (
    <Page>
      <PageHead title="Notes" subtitle="Documentation exceptions and family updates" />

      <div className="stack">
        {problem ? (
          <Notice tone="urgent" title="Could not complete that">
            {problem}
          </Notice>
        ) : null}

        {awaiting.length > 0 ? (
          <>
            <Notice tone="attention" title={`${awaiting.length} family update${awaiting.length === 1 ? '' : 's'} awaiting approval`}>
              Families do not see these visits until an update is approved.
            </Notice>

            {awaiting.map((update) => (
              <Card key={update.id}>
                <div className="row row--between row--top">
                  <div>
                    <h3>{update.headline}</h3>
                    <p className="meta">
                      {update.recipient_name} · {formatDate(update.scheduled_start)}
                    </p>
                  </div>
                  <div className="row">
                    {update.is_correction ? <Tag label="Correction" tone="info" /> : null}
                    {update.follow_up_needed ? <Tag label="Follow-up" tone="attention" /> : null}
                  </div>
                </div>

                <div className="divider" />

                <p className="kicker">What the family would see</p>
                <ul style={{ margin: 'var(--space-2) 0', paddingLeft: '1.1rem' }}>
                  {update.categories.map((category, index) => (
                    <li key={index}>
                      {category.name} — {category.outcome}
                      {category.detail ? <span className="meta"> ({category.detail})</span> : null}
                    </li>
                  ))}
                </ul>

                <div className="row">
                  <Button onClick={() => decide(update.id, 'approve')} busy={busyId === update.id}>
                    Approve and share
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => decide(update.id, 'withhold')}
                    busy={busyId === update.id}
                  >
                    Withhold
                  </Button>
                </div>
              </Card>
            ))}
          </>
        ) : null}

        <Card variant="flush">
          <CardHead title="Note queues" />
          <div style={{ padding: '0 var(--space-4)' }}>
            <Tabs
              label="Note queues"
              active={queue}
              onChange={setQueue}
              tabs={QUEUES.map((entry) => ({
                value: entry.value,
                label: entry.label,
                count: queues.data?.counts[entry.value] ?? 0,
              }))}
            />
          </div>

          <Table
            columns={['Client', 'Caregiver', 'Visit', 'Due', 'Note', '']}
            empty={`Nothing in the ${queue} queue.`}
          >
            {items.map((item) => (
              <tr
                key={item.visit_id}
                data-clickable={item.note_id ? 'true' : undefined}
                onClick={item.note_id ? () => navigate(`/notes/${item.note_id}`) : undefined}
              >
                <td className="strong">{item.recipient_name}</td>
                <td>{item.caregiver_name ?? <span className="meta">Unassigned</span>}</td>
                <td className="meta">{formatDate(item.scheduled_start)}</td>
                <td className="meta">
                  {item.note_due_at ? formatRelative(item.note_due_at) : '—'}
                </td>
                <td>
                  {item.note_status ? (
                    <Tag label={humanise(item.note_status)} tone="positive" />
                  ) : (
                    <Tag label="Not submitted" tone="attention" />
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div className="row" style={{ justifyContent: 'flex-end' }}>
                    {/* Only where there is no note yet, which is also the only
                        case where the row itself is not clickable. A visit with
                        a note takes the correction path, which preserves the
                        original. */}
                    {!item.note_id && item.caregiver_name ? (
                      <Button variant="secondary" onClick={() => setRecordingFor(item.visit_id)}>
                        Record note
                      </Button>
                    ) : null}
                    <Link to={`/clients/${item.care_recipient_id}`}>Open client →</Link>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      {recordingFor ? (
        <RecordNoteOnBehalf
          visitId={recordingFor}
          onClose={() => setRecordingFor(null)}
          onDone={() => {
            setRecordingFor(null);
            queues.reload();
            pending.reload();
          }}
        />
      ) : null}
    </Page>
  );
}
